// =========================
// HTML-to-PDF Service
// =========================

import { UnifiedInvoiceData } from './unifiedInvoiceService';

/**
 * Generate PDF using HTML-to-PDF conversion for exact UI replica
 * @param invoiceData - Invoice data
 * @returns Promise<Blob>
 */
export const generateHTMLToPDF = async (invoiceData: UnifiedInvoiceData): Promise<Blob> => {
  try {
    console.log('Starting HTML-to-PDF generation...');
    
    // Get logo as base64 first
    const logoBase64 = await getLogoBase64();
    console.log('Logo base64 obtained:', logoBase64 ? 'Yes' : 'No');
    
    // Create HTML template that matches UI exactly with actual logo
    const invoiceHTML = createInvoiceHTML(invoiceData, logoBase64);
    
    // Create a temporary container
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = invoiceHTML;
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.width = '800px';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    document.body.appendChild(tempContainer);

    // Wait for fonts and images to load
    await new Promise(resolve => setTimeout(resolve, 2000)); // Increased wait time for logo

    // Use html2canvas to capture the HTML as canvas
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(tempContainer, {
      scale: 2, // Higher quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 800,
      height: tempContainer.scrollHeight,
      logging: false
    });

    // Remove temporary container
    document.body.removeChild(tempContainer);

    // Convert canvas to PDF using jsPDF
    const { default: jsPDF } = await import('jspdf');
    const pdf = new jsPDF('p', 'mm', 'a4');

    const imgData = canvas.toDataURL('image/png');
    const finalPdfWidth = pdf.internal.pageSize.getWidth();
    const finalPdfHeight = (canvas.height * finalPdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, finalPdfWidth, finalPdfHeight);

    console.log('HTML-to-PDF generation completed successfully');
    
    // Convert PDF to blob
    const pdfBlob = pdf.output('blob');
    return pdfBlob;
    
  } catch (error) {
    console.error('Error generating HTML-to-PDF:', error);
    throw new Error('Failed to generate HTML-to-PDF');
  }
};

/**
 * Convert logo to base64 for PDF generation
 * @returns Promise<string>
 */
const getLogoBase64 = async (): Promise<string> => {
  try {
    // Import the logo image from the correct path
    const logoModule = await import('../assets/images/admin-logos/Invoice-logo.png');
    const response = await fetch(logoModule.default);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting logo to base64:', error);
    // Fallback to text-based logo if image fails
    return '';
  }
};

/**
 * Create HTML template that matches UI exactly
 * @param invoiceData - Invoice data
 * @param logoBase64 - Base64 encoded logo image
 * @returns HTML string
 */
const createInvoiceHTML = (invoiceData: UnifiedInvoiceData, logoBase64?: string): string => {
  return `
    <div style="
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px;
      background: white;
      color: #333;
      line-height: 1.6;
      box-sizing: border-box;
    ">
      <!-- Header -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px;">
        <div style="display: flex; align-items: center;">
          ${logoBase64 ? `
            <img src="${logoBase64}" alt="SFL Logo" style="
              width: 120px;
              height: 80px;
              object-fit: contain;
              margin-right: 20px;
            " />
          ` : `
            <div style="
              width: 120px;
              height: 80px;
              background: #0077F9;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 20px;
            ">
              <span style="color: white; font-size: 32px; font-weight: bold;">SFL</span>
            </div>
          `}
        </div>
        <div style="text-align: right; color: #666; font-size: 14px;">
          Issued on: ${invoiceData.issuedOn}
        </div>
      </div>

      <!-- Billing Sections -->
      <div style="display: flex; justify-content: space-between; margin-bottom: 40px; gap: 40px;">
        <!-- Billed From -->
        <div style="flex: 1;">
          <h3 style="font-size: 16px; font-weight: bold; color: #495057; margin-bottom: 20px; margin-top: 0;">Billed From:</h3>
          <div style="font-size: 16px; font-weight: bold; color: #0077F9; margin-bottom: 15px;">
            ${invoiceData.billedFrom.companyName}
          </div>
          <div style="font-size: 14px; color: #333; margin-bottom: 15px; line-height: 1.5;">
            ${invoiceData.billedFrom.address}
          </div>
          <div style="font-size: 14px; color: #0077F9; margin-bottom: 15px;">
            ${invoiceData.billedFrom.email}
          </div>
          <div style="font-size: 13px; color: #666;">
            GST ID: ${invoiceData.billedFrom.gstNumber}
          </div>
        </div>

        <!-- Billed To -->
        <div style="flex: 1;">
          <h3 style="font-size: 16px; font-weight: bold; color: #495057; margin-bottom: 20px; margin-top: 0;">Billed To:</h3>
          <div style="font-size: 16px; font-weight: bold; color: #333; margin-bottom: 15px;">
            ${invoiceData.billedTo.clubName}
          </div>
          ${invoiceData.billedTo.address ? `
            <div style="font-size: 14px; color: #333; margin-bottom: 15px; line-height: 1.5;">
              ${invoiceData.billedTo.address}
            </div>
          ` : ''}
          ${invoiceData.billedTo.gstNumber ? `
            <div style="font-size: 13px; color: #666;">
              GST ID: ${invoiceData.billedTo.gstNumber}
            </div>
          ` : ''}
        </div>
      </div>

      <!-- Payment Details Table -->
      <div style="margin-bottom: 40px;">
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="padding: 12px; text-align: left; font-weight: bold; text-transform: uppercase; color: #495057; border: 1px solid #dee2e6;">ITEM</th>
              <th style="padding: 12px; text-align: center; font-weight: bold; text-transform: uppercase; color: #495057; border: 1px solid #dee2e6;">TRANSACTION ID</th>
              <th style="padding: 12px; text-align: right; font-weight: bold; text-transform: uppercase; color: #495057; border: 1px solid #dee2e6;">TOTAL AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            ${invoiceData.items.map((item: any) => `
              <tr>
                <td style="padding: 12px; border: 1px solid #dee2e6;">
                  <div style="line-height: 1.4;">
                    <div style="font-weight: 600; margin-bottom: 4px;">
                      Tournament name: ${item.tournamentName || 'N/A'}
                    </div>
                    <div style="margin-bottom: 4px;">
                      Team name: ${item.teamName || 'N/A'}
                    </div>
                    <div>
                      Age category: ${item.ageCategory || 'N/A'}
                    </div>
                  </div>
                </td>
                <td style="padding: 12px; text-align: center; border: 1px solid #dee2e6;">${item.transactionId}</td>
                <td style="padding: 12px; text-align: right; border: 1px solid #dee2e6;">${formatCurrency(item.totalAmount)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <!-- Summary Table -->
      <div style="display: flex; justify-content: flex-end;">
        <div style="width: 400px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tbody>
              <tr style="background-color: #f8f9fa;">
                <td style="padding: 8px; border: 1px solid #dee2e6; font-weight: 500;">
                  <div style="line-height: 1.4;">
                    <div style="font-weight: 600; margin-bottom: 4px;">
                      Tournament name: ${invoiceData.items[0]?.tournamentName || 'N/A'}
                    </div>
                    <div style="margin-bottom: 4px;">
                      Team name: ${invoiceData.items[0]?.teamName || 'N/A'}
                    </div>
                    <div>
                      Age category: ${invoiceData.items[0]?.ageCategory || 'N/A'}
                    </div>
                  </div>
                </td>
                <td style="padding: 8px; border: 1px solid #dee2e6; text-align: right;">
                  <!-- Empty cell for alignment -->
                </td>
              </tr>
              <tr>
                <td style="padding: 8px; border: 1px solid #dee2e6; font-weight: 500;">Subtotal:</td>
                <td style="padding: 8px; border: 1px solid #dee2e6; text-align: right;">
                  ${formatCurrency(invoiceData.summary.subtotal)}
                </td>
              </tr>
              <tr style="background-color: #f8f9fa;">
                <td style="padding: 8px; border: 1px solid #dee2e6; font-weight: 500;">IGST(18%):</td>
                <td style="padding: 8px; border: 1px solid #dee2e6; text-align: right;">
                  ${formatCurrency(invoiceData.summary.igst)}
                </td>
              </tr>
              <tr style="background-color: #f8f9fa; color: #0077F9;">
                <td style="padding: 8px; border: 1px solid #dee2e6; font-weight: bold;">Total:</td>
                <td style="padding: 8px; border: 1px solid #dee2e6; text-align: right; font-weight: bold;">
                  ${formatCurrency(invoiceData.summary.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;
};

/**
 * Format currency for display
 * @param amount - Amount to format
 * @returns Formatted currency string
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};
